import type { AppendMessage } from '@assistant-ui/react'

import type { ModelMessage, NewUserContent } from '@/types/chat-v2'

/** A composer send made while a reply streams: the running reply takes it in instead of being replaced. */
export const QUEUED_SEND_METADATA: AppendMessage['metadata'] = {
  custom: { queued: true },
}

export const isQueuedSend = (
  message: Pick<AppendMessage, 'metadata'>,
): boolean => message.metadata?.custom?.queued === true

/** A prompt sent again from its ended run: it carries its own content, never the composer's draft. */
export const RESENT_PROMPT_METADATA: AppendMessage['metadata'] = {
  custom: { resent: true },
}

export const isResentPrompt = (
  message: Pick<AppendMessage, 'metadata'>,
): boolean => message.metadata?.custom?.resent === true

// Mirrors `QUEUE_ID_METADATA_KEY` in common_services chat_services/types.py.
const QUEUE_ID_METADATA_KEY = 'queue_id'

/** The queued message a request is, when a running run took it in. */
export const queuedMessageId = (message: ModelMessage): string | undefined => {
  const queueId =
    message.kind === 'request'
      ? message.metadata?.[QUEUE_ID_METADATA_KEY]
      : undefined
  return typeof queueId === 'string' ? queueId : undefined
}

/** The text a queued message's saved message will carry, cards inline. */
export const queuedMessageText = (
  content: string | readonly NewUserContent[],
): string => {
  if (typeof content === 'string') return content
  // A string right before a file part is that file's label, not typed text.
  return content
    .flatMap((part, index) => {
      if (typeof part !== 'string') {
        return part.kind === 'text-content' ? [part.content] : []
      }
      const next = content[index + 1]
      return next === undefined || typeof next === 'string' ? [part] : []
    })
    .join('\n\n')
}
